/**
 * ThreadSidebar — list / create / rename / switch / delete conversation
 * threads.
 *
 * Layout: an `<aside>` with a "New chat" header button and a vertical list of
 * thread summaries. Each row exposes inline rename and delete affordances.
 * Confirmation modals (via the shared `<Modal>` primitive) gate destructive
 * actions (Requirement 10.2). The active row is marked with both an
 * `aria-current="true"` attribute and a visible active style so the active
 * thread is discoverable to assistive tech and to the eye.
 *
 * Keyboard navigation (Task 21.2, Requirement 10.2):
 *   - `ArrowDown` / `ArrowUp` move focus between thread title buttons.
 *   - `Home` / `End` jump to the first / last thread.
 *   - `Enter` / `Space` activate the focused thread (default browser
 *     behaviour for buttons — no extra wiring needed).
 *
 * Validates: Requirements 4.3, 4.7, 9.3, 10.2, 10.3.
 */

import { useState } from 'react';
import type { KeyboardEvent, ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { useThreadsStore } from '../../state';
import { Modal } from '../common';
import { CharacterArt } from '../theme';

import styles from './ThreadSidebar.module.css';

export interface ThreadSidebarProps {
  /**
   * Optional callback fired after the user picks a thread. The chat
   * route uses this to auto-close the mobile drawer once a selection
   * is made; desktop callers can omit it.
   */
  onSelect?: () => void;
}

export function ThreadSidebar({
  onSelect,
}: ThreadSidebarProps = {}): ReactElement {
  const { t } = useTranslation();
  const summaries = useThreadsStore((s) => s.summaries);
  const activeThread = useThreadsStore((s) => s.activeThread);
  const createThread = useThreadsStore((s) => s.createThread);
  const selectThread = useThreadsStore((s) => s.selectThread);
  const renameThread = useThreadsStore((s) => s.renameThread);
  const deleteThread = useThreadsStore((s) => s.deleteThread);

  // Local UI state for the rename and delete modals. We keep the id of the
  // affected thread separate from the form value so the modal text stays
  // stable even if the underlying summary changes.
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [renameValue, setRenameValue] = useState<string>('');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const handleCreate = (): void => {
    void createThread(t('chat.newThread'));
    onSelect?.();
  };

  const handleSelect = (id: string): void => {
    if (activeThread?.id === id) {
      onSelect?.();
      return;
    }
    void selectThread(id);
    onSelect?.();
  };

  const openRename = (id: string, currentTitle: string): void => {
    setRenamingId(id);
    setRenameValue(currentTitle);
  };

  const openDelete = (id: string): void => {
    setDeletingId(id);
  };

  const confirmRename = async (): Promise<void> => {
    if (renamingId === null) return;
    const trimmed = renameValue.trim();
    if (trimmed.length === 0) {
      setRenamingId(null);
      return;
    }
    await renameThread(renamingId, trimmed);
    setRenamingId(null);
  };

  const confirmDelete = async (): Promise<void> => {
    if (deletingId === null) return;
    await deleteThread(deletingId);
    setDeletingId(null);
  };

  // We don't have a dedicated `common.cancel` key in the i18n bundles yet,
  // so we fall back to a stable English literal. The bundle can grow that
  // key later without changing this component's surface.
  const cancel = 'Cancel';

  /**
   * Arrow-key navigation between thread items (Requirement 10.2).
   *
   * Attached per-button so jsx-a11y does not flag a non-interactive
   * `<ul>` carrying a keydown listener. The handler walks the
   * sibling `[data-thread-item]` buttons so the focus order tracks
   * the visual order even after React re-orders rows.
   */
  const handleItemKeyDown = (event: KeyboardEvent<HTMLButtonElement>): void => {
    const { key } = event;
    if (
      key !== 'ArrowDown' &&
      key !== 'ArrowUp' &&
      key !== 'Home' &&
      key !== 'End'
    ) {
      return;
    }
    const list = event.currentTarget.closest('ul');
    if (list === null) return;
    const items = Array.from(
      list.querySelectorAll<HTMLButtonElement>('[data-thread-item="true"]'),
    );
    if (items.length === 0) return;
    const currentIdx = items.indexOf(event.currentTarget);
    let nextIdx = currentIdx;
    if (key === 'ArrowDown') {
      nextIdx = Math.min(currentIdx + 1, items.length - 1);
    } else if (key === 'ArrowUp') {
      nextIdx = Math.max(currentIdx - 1, 0);
    } else if (key === 'Home') {
      nextIdx = 0;
    } else if (key === 'End') {
      nextIdx = items.length - 1;
    }
    if (nextIdx === currentIdx || nextIdx < 0) return;
    event.preventDefault();
    items[nextIdx]?.focus();
  };

  return (
    <aside
      className={styles.sidebar}
      aria-label={t('a11y.openSidebar')}
      data-testid="thread-sidebar"
    >
      <header className={styles.header}>
        <button
          type="button"
          onClick={handleCreate}
          className={styles.newBtn}
          data-testid="thread-sidebar-new"
        >
          + {t('chat.newThread')}
        </button>
      </header>

      <ul className={styles.list} role="list">
        {summaries.map((s) => {
          const isActive = activeThread?.id === s.id;
          return (
            <li
              key={s.id}
              className={[styles.item, isActive ? styles.itemActive : '']
                .filter(Boolean)
                .join(' ')}
              aria-current={isActive ? 'true' : undefined}
            >
              <button
                type="button"
                onClick={() => handleSelect(s.id)}
                onKeyDown={handleItemKeyDown}
                className={styles.itemTitle}
                title={s.title}
                data-thread-item="true"
              >
                <span className={styles.itemTitleText}>{s.title}</span>
                <span className={styles.itemMeta}>{s.messageCount}</span>
              </button>
              <div className={styles.itemActions}>
                <button
                  type="button"
                  onClick={() => openRename(s.id, s.title)}
                  className={styles.iconBtn}
                  aria-label={`${t('chat.renameThread')}: ${s.title}`}
                  title={t('chat.renameThread')}
                >
                  ✏️
                </button>
                <button
                  type="button"
                  onClick={() => openDelete(s.id)}
                  className={styles.iconBtn}
                  aria-label={`${t('chat.deleteThread')}: ${s.title}`}
                  title={t('chat.deleteThread')}
                >
                  🗑️
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      {/* Mascot footer — the active theme's character art lives here so
          it stays out of the chat surface and never overlaps messages. */}
      <div className={styles.mascot} aria-hidden="true">
        <CharacterArt />
      </div>

      <Modal
        open={renamingId !== null}
        title={t('chat.renameThread')}
        onClose={() => setRenamingId(null)}
        primaryAction={{
          label: t('chat.renameThread'),
          onClick: () => {
            void confirmRename();
          },
        }}
        secondaryAction={{ label: cancel, onClick: () => setRenamingId(null) }}
      >
        <input
          type="text"
          value={renameValue}
          onChange={(e) => setRenameValue(e.target.value)}
          className={styles.renameInput}
          aria-label={t('chat.renameThread')}
          autoFocus
        />
      </Modal>

      <Modal
        open={deletingId !== null}
        title={t('chat.deleteThread')}
        onClose={() => setDeletingId(null)}
        primaryAction={{
          label: t('chat.deleteThread'),
          tone: 'destructive',
          onClick: () => {
            void confirmDelete();
          },
        }}
        secondaryAction={{ label: cancel, onClick: () => setDeletingId(null) }}
      >
        <p className={styles.modalBody}>{t('chat.deleteThreadConfirm')}</p>
      </Modal>
    </aside>
  );
}
